// =============================================================================
// API Client - Axios Configuration
// =============================================================================

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

/**
 * Risolve sull'host dell'API i path assoluti restituiti dal backend
 * (es. il logo dell'organizzazione: `/api/v1/tenants/:id/logo?v=...`).
 * Gli URL già assoluti vengono lasciati intatti.
 */
export function resolveAssetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  try {
    return new URL(path, new URL(API_BASE_URL, window.location.origin)).toString();
  } catch {
    return path;
  }
}

/// Codici di errore che accompagnano un 401 "password di conferma sbagliata",
/// non un 401 "sessione non valida".
const PASSWORD_CONFIRMATION_ERRORS = new Set([
  'AUTH_WRONG_PASSWORD',
  'AUTH_ACCOUNT_NO_PASSWORD',
]);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Non tutti i 401 sono sessioni scadute: quando un'operazione
          // sensibile chiede di riconfermare la password (cambio password,
          // eliminazione organizzazione) il backend risponde 401 se la
          // password digitata e' sbagliata. La sessione li' e' validissima:
          // sloggare l'utente sarebbe un effetto collaterale assurdo, e gli
          // farebbe anche perdere il messaggio di errore.
          if (PASSWORD_CONFIRMATION_ERRORS.has(error.response.data?.errorCode)) {
            break;
          }

          // Handle unauthorized - Logout e redirect a login
          console.error('Unauthorized - session expired or invalid');

          // Redirect solo se c'era una sessione reale: zustand persist scrive
          // la chiave anche da sloggato (token null) e un 401 su una pagina
          // pubblica (es. /register) non deve buttare fuori il visitatore.
          const authStorage = localStorage.getItem('skedulab-auth');
          let hadToken = false;
          try {
            hadToken = !!JSON.parse(authStorage || '{}')?.state?.token;
          } catch {
            hadToken = !!authStorage;
          }
          if (hadToken) {
            localStorage.removeItem('skedulab-auth');
            // Reload per forzare redirect a login
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
          }
          break;
        case 403:
          console.error('Forbidden - insufficient permissions');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
      }
    } else if (error.request) {
      console.error('Network error - no response received');
    }
    return Promise.reject(error);
  }
);

export default apiClient;
